import React from 'react';
import { 
  LayoutDashboard, 
  Kanban, 
  Bot, 
  Users, 
  Clock, 
  Settings, 
  Zap,
  ChevronRight,
  ShieldCheck
} from 'lucide-react';

export default function Sidebar({ activeTab, setActiveTab, projects, currentProjectId, setCurrentProjectId }) {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'projects', label: 'Projects & Boards', icon: Kanban },
    { id: 'ai', label: 'AI Studio & Workflows', icon: Bot, badge: 'AI' },
    { id: 'team', label: 'Team Capacity', icon: Users },
    { id: 'time', label: 'Time Tracking', icon: Clock },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col h-screen border-r border-slate-800 flex-shrink-0 select-none">
      {/* Brand Header */}
      <div className="p-6 border-b border-slate-800/80 flex items-center space-x-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-500/30">
          <Zap className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-base font-bold text-white tracking-wide">NexusFlow</h1>
          <p className="text-[11px] font-medium text-indigo-400 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Enterprise AI v2.4
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        <div>
          <p className="px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Workspace</p>
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition group ${
                    isActive 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' 
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isActive ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-800 text-indigo-400'}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Active Projects Quick Switch */}
        <div>
          <div className="flex items-center justify-between px-3 mb-2">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Active Projects</p>
            <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded font-mono">{projects.length}</span>
          </div>
          <div className="space-y-1">
            {projects.map((proj) => {
              const isSelected = activeTab === 'projects' && currentProjectId === proj.id;
              return (
                <button
                  key={proj.id}
                  onClick={() => {
                    setCurrentProjectId(proj.id);
                    setActiveTab('projects');
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition group ${
                    isSelected 
                      ? 'bg-slate-800 text-white font-medium border-l-2 border-indigo-500' 
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                  }`}
                >
                  <div className="flex items-center space-x-2 truncate">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"></span>
                    <span className="truncate">{proj.name}</span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* User Footer */}
      <div className="p-4 border-t border-slate-800/80 bg-slate-950/40 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img 
            src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150" 
            alt="Admin" 
            className="w-9 h-9 rounded-full object-cover ring-2 ring-indigo-500/50"
          />
          <div className="overflow-hidden">
            <p className="text-xs font-bold text-white truncate">Elena Rostova</p>
            <p className="text-[11px] text-slate-400 truncate">elena.r@nexusflow.io</p>
          </div>
        </div>
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Online"></div>
      </div>
    </aside>
  );
}
